<template>
  <v-container fluid class="pb-12">
    <v-row class="align-center px-3 pt-3 mb-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-3">
          <v-icon color="white" small>fas fa-truck-ramp-box</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text">Guías de despacho masivas</div>
          <div class="caption grey--text">Distribuciones por local de las OT (cargadas por ventas o aquí); cada una genera sus guías, emitidas a Facturación.cl a ritmo controlado</div>
        </div>
      </div>
      <v-spacer />
      <v-btn text small class="mr-2" @click="$router.push({ name: 'Invoices' })"><v-icon left small>fas fa-arrow-left</v-icon>Facturación</v-btn>
      <v-btn icon class="mr-2" :loading="refreshing" title="Actualizar" @click="manualRefresh"><v-icon small>fas fa-arrows-rotate</v-icon></v-btn>
      <v-btn color="primary" class="font-weight-bold" @click="openImport(filterOt)"><v-icon left small>fas fa-plus</v-icon>Nueva distribución</v-btn>
    </v-row>

    <v-card outlined>
      <div class="pa-3 d-flex flex-wrap align-center" style="gap: 12px">
        <v-text-field
          v-model="filterOt" dense outlined hide-details clearable type="number" label="OT" prepend-inner-icon="fas fa-hashtag"
          style="max-width: 160px"
        />
        <v-text-field
          v-model="search" dense outlined hide-details clearable label="Buscar cliente o distribución"
          prepend-inner-icon="fas fa-search" style="max-width: 320px"
        />
        <v-checkbox v-model="onlyPending" dense hide-details class="mt-0" label="Solo pendientes de emisión" />
      </div>
      <v-data-table
        :headers="headers" :items="filtered" :loading="loading" dense :items-per-page="25"
        :no-data-text="filterOt ? 'La OT ' + filterOt + ' no tiene distribuciones' : 'No hay distribuciones'"
        class="row-pointer" @click:row="open"
      >
        <template v-slot:item.id="{ item }">
          <span class="font-weight-bold">#{{ item.id }}</span>
          <span v-if="item.name" class="ml-1">{{ item.name }}</span>
        </template>
        <template v-slot:item.workorder="{ item }">OT {{ item.workorder }}</template>
        <template v-slot:item.distribution_status="{ item }">
          <v-chip x-small :color="distOf(item).color" dark>{{ distOf(item).text }}</v-chip>
        </template>
        <template v-slot:item.status="{ item }">
          <v-chip x-small :color="statusOf(item).color" dark>{{ statusOf(item).text }}</v-chip>
        </template>
        <template v-slot:item.progress="{ item }">
          <div style="min-width: 160px">
            <div class="caption">
              {{ item.summary.emitted }} / {{ item.summary.total }} emitidas
              <span v-if="item.summary.counts.error" class="error--text"> · {{ item.summary.counts.error }} error</span>
              <span v-if="item.summary.counts.revisar" class="warning--text"> · {{ item.summary.counts.revisar }} revisar</span>
            </div>
            <v-progress-linear rounded height="5" :value="item.summary.total ? (item.summary.emitted / item.summary.total) * 100 : 0" :color="statusOf(item).color" />
          </div>
        </template>
        <template v-slot:item.created_at="{ item }">{{ item.created_at | moment('DD/MM/YYYY HH:mm') }}</template>
      </v-data-table>
    </v-card>

    <bulk-dispatch-import-dialog v-model="importDialog" :initial-ot="initialOt" @loaded="onLoaded" />
  </v-container>
</template>

<script>
import notifications from '@/utils/notifications'
import BulkDispatchImportDialog from '@/components/facturacioncl/BulkDispatchImportDialog.vue'
import { listDispatchBatches } from '@/api/facturacioncl'
import { DISTRIBUTION_STATUS } from '@/utils/distributions'

export default {
  name: 'BulkDispatch',
  components: { BulkDispatchImportDialog },
  data() {
    return {
      batches: [],
      loading: false,
      refreshing: false,
      importDialog: false,
      initialOt: null,
      filterOt: this.$route.query.ot ? String(this.$route.query.ot) : '',
      search: '',
      onlyPending: !this.$route.query.ot,
      timer: null,
      headers: [
        { text: 'Distribución', value: 'id' },
        { text: 'OT', value: 'workorder', width: 90 },
        { text: 'Cliente', value: 'customer_name' },
        { text: 'Estado', value: 'distribution_status', width: 120 },
        { text: 'Emisión', value: 'status', width: 110 },
        { text: 'Avance', value: 'progress', sortable: false },
        { text: 'Creado', value: 'created_at', width: 140 },
        { text: 'Por', value: 'created_by_name', width: 140 },
      ],
    }
  },
  computed: {
    filtered() {
      const q = (this.search || '').toLowerCase().trim()
      return this.batches.filter(b => {
        if (!q) return true
        return [b.customer_name, b.customer_rut, b.name, b.file_name, '#' + b.id].some(v => (v || '').toLowerCase().includes(q))
      })
    },
  },
  mounted() {
    this.fetch()
  },
  watch: {
    // Desde una OT: la lista se filtra por esa OT
    '$route.query.ot'(ot) {
      this.filterOt = ot ? String(ot) : ''
      if (ot) this.onlyPending = false
    },
    filterOt() {
      if (this.otTimer) clearTimeout(this.otTimer)
      this.otTimer = setTimeout(() => { this.batches = []; this.fetch() }, 500)
    },
    onlyPending() {
      this.batches = []
      this.fetch()
    },
  },
  beforeDestroy() {
    this.destroyed = true
    if (this.timer) clearInterval(this.timer)
  },
  methods: {
    distOf(b) {
      return DISTRIBUTION_STATUS[b.distribution_status] || { text: b.distribution_status, color: 'grey' }
    },
    statusOf(b) {
      const s = b.summary
      if (s.interrupted) return { text: 'Interrumpido', color: 'warning' }
      if (b.status === 'running') return { text: 'Emitiendo', color: 'primary' }
      if (b.status === 'paused') return { text: 'Pausado', color: 'warning' }
      if (s.total && s.emitted === s.total) return { text: 'Terminado', color: 'success' }
      return { text: 'Detenido', color: 'grey' }
    },
    async fetch() {
      this.loading = !this.batches.length
      try {
        const params = {}
        if (this.filterOt) params.workorder = String(this.filterOt).trim()
        if (this.onlyPending) params.pending = 1
        this.batches = await listDispatchBatches(params)
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudieron cargar las distribuciones')
      } finally {
        this.loading = false
      }
      if (this.destroyed) return
      const active = this.batches.some(b => b.status === 'running' && !b.summary.interrupted)
      if (active && !this.timer) this.timer = setInterval(this.fetch, 5000)
      if (!active && this.timer) { clearInterval(this.timer); this.timer = null }
    },
    async manualRefresh() {
      this.refreshing = true
      try { await this.fetch() } finally { this.refreshing = false }
    },
    openImport(ot = null) {
      this.initialOt = ot
      this.importDialog = true
    },
    open(batch) {
      this.$router.push({ name: 'BulkDispatchBatch', params: { batchId: batch.id } })
    },
    onLoaded(batch) {
      this.open(batch)
    },
  },
}
</script>

<style scoped>
.row-pointer >>> tbody tr { cursor: pointer; }
</style>
